import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M11 L11.09 — Rework Screen.
 * Shows rework history, current rework cycle, and reasons.
 */
interface ReworkEntry {
  readonly entryId: string;
  readonly cycleNo: number;
  readonly reason: string;
  readonly requestedBy: string;
  readonly requestedAt: string;
  readonly status: string;
  readonly completedAt: string | null;
  readonly remarks: string | null;
}

export function ReworkPage(): ReactNode {
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { verificationId } = useParams<{ verificationId: string }>();
  const auth = useAuth();
  const [entries, setEntries] = useState<ReworkEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<ReworkEntry[]>(`/v1/closure/review/${verificationId}/rework`, { token: auth.token })
      .then((d) => { if (alive) setEntries(d); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load rework history'); });
    return () => { alive = false; };
  }, [auth.token, verificationId]);

  if (error) return <main aria-label="rework"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!entries) return <main aria-label="rework"><p>{t('closure.rework.loading')}</p></main>;

  return (
    <main aria-label="rework">
      <h2>{t('closure.rework.title')}</h2>
      <p><Link to={`/closure/final/review/${verificationId}`}>{t('closure.rework.back')}</Link></p>

      {entries.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>{t('closure.rework.empty')}</p>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={th}>{t('closure.rework.col.cycle')}</th>
              <th style={th}>{t('closure.rework.col.reason')}</th>
              <th style={th}>{t('closure.rework.col.requestedBy')}</th>
              <th style={th}>{t('closure.rework.col.requestedAt')}</th>
              <th style={th}>{t('closure.rework.col.status')}</th>
              <th style={th}>{t('closure.rework.col.completedAt')}</th>
              <th style={th}>{t('closure.rework.col.remarks')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.entryId} data-testid={`rework-${e.cycleNo}`}>
                <td style={td}>{e.cycleNo}</td>
                <td style={td}>{e.reason}</td>
                <td style={td}>{e.requestedBy}</td>
                <td style={td}>{at(e.requestedAt)}</td>
                <td style={td}><strong>{e.status}</strong></td>
                <td style={td}>{at(e.completedAt)}</td>
                <td style={td}>{e.remarks ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid var(--color-border-default)' };
const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--color-border-default)' };
